import { useId } from 'react';
import type { ReactNode } from 'react';
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from 'react-hook-form';
import type {
  FieldValues,
  RefCallBack,
  Resolver,
  SubmitHandler,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { Button } from './Button';
import type { ButtonProps } from './Button';
import { Stack } from './Layout';

// Form primitive (story 2-93, G-26). A THIN wrapper over react-hook-form —
// state, validation and focus management stay RHF's job; this file only
// wires the zod resolver and renders the DS-shaped label/description/error
// chrome around whatever control a page composes. One primitive, two
// regimes: `useZodForm` takes ANY z.ZodType, whether it's a generated
// contract schema (`lib/api/<module>.gen.ts`, composed with .omit()/.extend()
// per G-26) or one assembled at runtime from FormSchema v2 (the dynamic
// capture regime — see the "Forms" gallery section for both).

// ---------------------------------------------------------------- useZodForm

/**
 * Wraps `useForm` with the zod resolver wired in and `shouldFocusError: true`
 * (RHF moves focus to the first invalid control on submit — the a11y
 * contract this primitive relies on instead of per-field `role="alert"`).
 * Generic over the schema so `z.input<TSchema>` types the form values,
 * whatever TSchema is — a `const` export from a `.gen.ts` module or a
 * schema built at runtime (`z.object(...)` assembled from FormSchema v2).
 */
export function useZodForm<TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  options?: Omit<UseFormProps<z.input<TSchema>>, 'resolver'>,
): UseFormReturn<z.input<TSchema>> {
  // The resolver's Output type param can differ from Input (zod transforms);
  // this primitive only needs Input-shaped values, so the cast collapses
  // that to the single type param useForm is instantiated with below.
  const resolver = zodResolver(schema) as Resolver<z.input<TSchema>>;
  return useForm<z.input<TSchema>>({
    resolver,
    shouldFocusError: true,
    ...options,
  });
}

// ---------------------------------------------------------------------- Form

export interface FormProps<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  onSubmit: SubmitHandler<TFieldValues>;
  children: ReactNode;
  className?: string;
  /**
   * Forwarded onto the native `<form>` element. Only needed when a submit
   * control lives OUTSIDE this element and links back via the HTML `form`
   * attribute — the `ModalFooter` action-bar pattern `ActionHosts.tsx`'s own
   * `htmlType`/`form` fields exist for (story 2-95b: `CreateInspectionDialog`
   * is the first `Form`-primitive consumer with its submit button in a
   * modal footer rather than inside the form body). Omit for the common case
   * (`FormSubmit` rendered as a normal child).
   */
  id?: string;
}

/** `<form>` wired to RHF's `handleSubmit`, `noValidate` (zod owns validation,
 * not the browser), wrapped in `FormProvider` so `FormField` below reads
 * form state via context instead of prop drilling. */
export function Form<TFieldValues extends FieldValues>({
  form,
  onSubmit,
  children,
  className,
  id,
}: FormProps<TFieldValues>): JSX.Element {
  return (
    <FormProvider {...form}>
      <form id={id} onSubmit={form.handleSubmit(onSubmit)} noValidate className={className}>
        {children}
      </form>
    </FormProvider>
  );
}

// ----------------------------------------------------------------- FormField

// Everything a control needs to be a fully wired, accessible field. Spread
// this onto the DS Input/Select/Textarea/Checkbox/... (`<Input {...field} />`).
// Generic over the value type, DEFAULTED to the original text-ish union so
// every existing `<Input {...field} />` / `<Select {...field} />` call site
// keeps compiling and rendering unchanged — only a caller that explicitly
// instantiates `FormField<TValue>` (story 2-96: boolean/string[]/number
// controls) sees a different `value` type.
export interface FormFieldRenderProps<TValue = string | number | readonly string[] | undefined> {
  id: string;
  name: string;
  onChange: (...event: unknown[]) => void;
  onBlur: () => void;
  ref: RefCallBack;
  value: TValue;
  invalid: boolean;
  'aria-describedby'?: string;
  'aria-required'?: true;
}

export interface FormFieldProps<TValue = string | number | readonly string[] | undefined> {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
  /**
   * Chrome + value-passing mode (story 2-96, G-22/G-26 — one `FormField`
   * primitive absorbs every control shape rather than growing a parallel
   * wrapper per shape):
   *  - `'text'` (default): today's behaviour, BYTE-IDENTICAL — `<label
   *    htmlFor>` above the control, value coerced to `''` when nullish
   *    (Input/Textarea/Select all want a defined string-ish DOM value).
   *  - `'inline'`: control first, label to its right (checkbox, switch) —
   *    still a `<label htmlFor>` pointing at the one control, just laid out
   *    control-then-label instead of label-then-control. Value passed
   *    through RAW (no `?? ''` — a checkbox's value is a boolean; coercing
   *    it to `''` would be actively wrong).
   *  - `'group'`: `<fieldset>` + `<legend>` instead of `<label htmlFor>`,
   *    because a radio group / multiselect / rating has no single control
   *    an `htmlFor` could point at — the accessible group name comes from
   *    the legend, associated with every control inside via the fieldset's
   *    implicit "group" role. Value passed through raw, same reasoning as
   *    `'inline'`.
   */
  control?: 'text' | 'inline' | 'group';
  /**
   * Visually hides the label (`sr-only`) while it stays the control's real
   * accessible name via the same `<label htmlFor>` element — NEVER removed
   * from the accessibility tree, only hidden from sighted layout. For a
   * dense, chrome-free control (story 2-95b: `AgentPanel`'s message
   * composer, a one-line input+Send row in a fixed-height footer bar, where
   * a second visible label line above it would break the row) where the
   * placeholder or surrounding UI already carries the visible meaning. Only
   * implemented for `'text'` mode (the one real consumer) — `'inline'`'s
   * label sits beside a checkbox/switch, where hiding it would leave the
   * control meaning nothing on its own, and `'group'` mode's `<legend>` is
   * not a candidate: a fieldset with no visible legend is a different,
   * harder a11y case this prop deliberately does not reach for.
   */
  hideLabel?: boolean;
  /**
   * Applied to this field's OWN outer wrapper (the `Stack`/`fieldset` every
   * mode renders), not the inner control — for when the field itself needs
   * to participate in a surrounding flex/grid row (e.g. `flex-1` so a
   * message composer's field grows to fill the row beside a `FormSubmit`
   * button). Putting `flex-1` on the CONTROL inside `children` instead is a
   * no-op: the control's direct parent is this wrapper, not the outer flex
   * row, so `flex-1` there has nothing to size against (a real bug found
   * live in `AgentPanel.tsx`'s composer row and this gallery's own
   * `HideLabelComposerDemo` — both had it on the `Input`, silently
   * ineffective in both).
   */
  className?: string;
  /** Render-prop, not cloneElement — explicit and type-safe, and it forces
   * composition of a DS primitive (G-22) at every call site. */
  children: (field: FormFieldRenderProps<TValue>) => ReactNode;
}

export function FormField<TValue = string | number | readonly string[] | undefined>({
  name,
  label,
  description,
  required,
  control = 'text',
  hideLabel = false,
  className = '',
  children,
}: FormFieldProps<TValue>): JSX.Element {
  // Renamed from the original `control` to avoid shadowing the new chrome-mode
  // prop of the same name — this is react-hook-form's `Control` object.
  const { control: rhfControl } = useFormContext();
  const { field, fieldState } = useController({ name, control: rhfControl });
  const controlId = useId();
  const descriptionId = useId();
  const errorId = useId();

  const errorMessage = fieldState.error?.message;
  const describedBy =
    [description ? descriptionId : null, errorMessage ? errorId : null].filter(Boolean).join(' ') ||
    undefined;

  // 'text' keeps the historical `?? ''` coercion (Input/Textarea/Select all
  // want a defined string-ish DOM value); 'inline'/'group' controls own
  // non-string-ish values (boolean, string[], number), so they get it raw.
  const value = (
    control === 'text' ? ((field.value as FormFieldRenderProps['value']) ?? '') : field.value
  ) as TValue;

  const renderProps: FormFieldRenderProps<TValue> = {
    id: controlId,
    name: field.name,
    onChange: field.onChange,
    onBlur: field.onBlur,
    ref: field.ref,
    value,
    invalid: fieldState.invalid,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(required ? { 'aria-required': true as const } : {}),
  };

  // The marker is a SIBLING, not a label/legend descendant: nesting it inside
  // <label>/<legend> put the "*" character into the accessible-name-bearing
  // element's raw text, which broke exact-string label lookups (e.g.
  // Playwright's getByLabel(name, { exact: true }), the story-2-40 e2e gate)
  // even though the *computed* accessible name correctly excludes
  // aria-hidden content — the two aren't the same algorithm in every
  // consumer. Semantics still come from aria-required on the control(s).
  const requiredMarker = required && (
    <span aria-hidden="true" className="text-destructive">
      *
    </span>
  );
  // Story 2-147ua's structural crowding fix (P10 §2.3, DOD §4 G-26). Direct
  // user feedback found this exact class of bug TWICE before this file's own
  // wrapper existed — `SectionNav.tsx`'s row count sitting beside its label
  // and `TemplatePublishBar.tsx`'s status description sitting beside its
  // chip, both cropping/crowding once the text ran long, both fixed by hand
  // by giving the text its OWN block below the thing it was crowding
  // (`flex flex-col gap-0.5`, `min-w-0 flex-1` on the text column). That fix
  // lived in two page-level files, not in the one primitive every field on
  // every form composes — so the same class was free to recur a third time
  // anywhere a page hand-rolled a control+message row. It cannot recur here:
  // `break-words` stops a long, space-less token (a validation message
  // quoting a URL or an id) from forcing this element past its own
  // intrinsic width — the failure mode `min-w-0`/`flex-1` alone does not
  // cover, since those only stop this field's CONTAINER stealing width from
  // a flex/grid sibling (guaranteed below), not stop the text itself
  // overflowing sideways inside that container.
  const descriptionBlock = description && (
    <p id={descriptionId} className="break-words text-xs text-muted-foreground">
      {description}
    </p>
  );
  const errorBlock = errorMessage && (
    <p id={errorId} className="break-words text-xs text-destructive">
      {errorMessage}
    </p>
  );

  if (control === 'group') {
    return (
      <fieldset
        className={`min-w-0 space-y-1.5 ${className}`}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      >
        <legend className="flex items-center gap-1 text-sm font-medium text-foreground">
          <span className="break-words">{label}</span>
          {requiredMarker}
        </legend>
        {descriptionBlock}
        {children(renderProps)}
        {errorBlock}
      </fieldset>
    );
  }

  if (control === 'inline') {
    return (
      // `min-w-0`: the OTHER half of the structural guarantee. A FormField
      // is routinely one item in a flex/grid row (two fields side by side in
      // a `Grid`, a field beside an action in a `Cluster`) — without this, a
      // long label/description/error forces this element's flex/grid track
      // wider than its fair share, which is exactly what SHRINKS a sibling
      // control or action. `Stack` (2-147sa) supplies the vertical rhythm
      // inside the field itself (label/description/control/error never
      // share a row, so none of them can compete with each other for
      // width); `min-w-0` is what stops the field as a WHOLE from
      // encroaching on whatever sits beside it. The action side of this
      // guarantee needs no matching change here: every DS `Button`
      // (`Button.tsx`'s `.btn-glass`) is already `white-space: nowrap`, so
      // its own min-content floor is its full label width — it structurally
      // cannot be compressed into a wrapped/truncated blob by a shrinking
      // flex row, only ever pushed onto a new line by `flex-wrap`.
      <Stack gap={1.5} className={`min-w-0 ${className}`}>
        <div className="flex items-center gap-2">
          {children(renderProps)}
          <label htmlFor={controlId} className="break-words text-sm font-medium text-foreground">
            {label}
          </label>
          {requiredMarker}
        </div>
        {descriptionBlock}
        {errorBlock}
      </Stack>
    );
  }

  // control === 'text' — same label/description/control/error stack as
  // before 2-96, now via `Stack` + the `min-w-0`/`break-words` guarantee
  // above (visually byte-identical: `Stack`'s default `gap=1.5` renders the
  // same `space-y-1.5` this div always used).
  return (
    <Stack gap={1.5} className={`min-w-0 ${className}`}>
      <div className={hideLabel ? 'sr-only' : 'flex items-center gap-1'}>
        <label htmlFor={controlId} className="break-words text-sm font-medium text-foreground">
          {label}
        </label>
        {requiredMarker}
      </div>
      {descriptionBlock}
      {children(renderProps)}
      {errorBlock}
    </Stack>
  );
}

// ----------------------------------------------------------------- FormError

export interface FormErrorProps {
  message?: string | null;
}

/** Form-level / server-error banner (e.g. the accept-invite 410). Unlike
 * FormField's inline errors, nothing receives focus here, so this DOES get
 * `role="alert"`. Renders nothing when passed no message. */
export function FormError({ message }: FormErrorProps): JSX.Element | null {
  if (!message) return null;
  // `break-words` for the same reason `FormField`'s description/error blocks
  // carry it (story 2-147ua): a server sentence can legitimately quote a
  // long unbroken token (an id, a URL), and this banner sits ABOVE
  // `FormActions`' own Cancel/Submit row in every real consumer — it must
  // never be allowed to overflow sideways and crowd that row rather than
  // simply growing taller.
  return (
    <p role="alert" className="break-words rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {message}
    </p>
  );
}

// ---------------------------------------------------------------- FormSubmit

export interface FormSubmitProps extends Omit<ButtonProps, 'type'> {
  /** Label shown while `formState.isSubmitting` is true; falls back to `children`. */
  pendingLabel?: string;
}

/** Submit button composing the DS Button; disables and swaps its label
 * while the form is submitting. */
export function FormSubmit({ children, pendingLabel, disabled, ...rest }: FormSubmitProps): JSX.Element {
  const { formState } = useFormContext();
  const pending = formState.isSubmitting;
  return (
    <Button type="submit" disabled={disabled || pending} {...rest}>
      {pending && pendingLabel ? pendingLabel : children}
    </Button>
  );
}
